import { RequestApi } from "./index";
import { API_URL } from "./helpers";
import type { Insights360 } from "./ThreeSixty";

/**
 * `insights` JSON on legacy 360 rows: newer rows match Insights360; older imports
 * use per-source counts (e.g. Lexis / Fama report).
 */
export type Legacy360ListInsights = Insights360 & {
  total?: number;
  summary?: number;
  legal_case?: number;
  fama_report?: number;
  lexis_nexis_news?: number;
  lexis_nexis_cases?: number;
};

export interface Legacy360ReportsApi {
  report_uuid: string;
  person_name: string;
  company_uuid: string;
  company_name: string;
  report_created: string | null;
  report_finished: string | null;
  blueprint_name: string;
  assignment_number: string | null;
  insights: Legacy360ListInsights | null;
  tags: Record<string, unknown> | null;
}

export interface Legacy360ReportsListMetadataApi {
  current_page: number;
  page_size: number;
  total_results: number;
}

export interface Legacy360ReportsListResponseApi {
  metadata: Legacy360ReportsListMetadataApi;
  results: Legacy360ReportsApi[];
}

export interface Legacy360ReportsVisibleResponseApi {
  visible: boolean;
}

export async function fetchLegacy360ReportsVisible(
  requestApi: RequestApi
): Promise<Legacy360ReportsVisibleResponseApi> {
  const res = await requestApi.get(`${API_URL}/360/legacy_reports/visible`);
  return res.data;
}

export interface Legacy360ListQueryParams {
  page?: number;
  page_size?: number;
  search?: string;
  company_uuids_csv?: string;
}

export async function fetchLegacy360ReportsList(
  requestApi: RequestApi,
  params: Legacy360ListQueryParams
): Promise<Legacy360ReportsListResponseApi> {
  const q: Record<string, string> = {};
  if (params.page !== undefined) q.page = String(params.page);
  if (params.page_size !== undefined) q.page_size = String(params.page_size);
  if (params.search) q.search = params.search;
  if (params.company_uuids_csv) q.company_uuids_csv = params.company_uuids_csv;
  const res = await requestApi.get(`${API_URL}/360/legacy_reports`, q);
  return res.data;
}

export interface Legacy360PresignedDownload {
  report_url: string;
  /** Same basename as S3 Content-Disposition; use for client-side save dialog. */
  filename?: string;
}

/** Presigned GET URL for legacy 360 report PDF (`{report_uuid}.pdf` in PDF bucket). */
export async function fetchLegacy360ReportPdfPresignedUrl(
  requestApi: RequestApi,
  reportUuid: string
): Promise<Legacy360PresignedDownload> {
  const res = await requestApi.get(
    `${API_URL}/360/legacy_reports/${reportUuid}/pdf`
  );
  return res.data as Legacy360PresignedDownload;
}

/** Presigned GET URL for legacy 360 consent object (S3 key = `report_uuid`). */
export async function fetchLegacy360ConsentFormPresignedUrl(
  requestApi: RequestApi,
  reportUuid: string
): Promise<Legacy360PresignedDownload> {
  const res = await requestApi.get(
    `${API_URL}/360/legacy_reports/${reportUuid}/consent_form`
  );
  return res.data as Legacy360PresignedDownload;
}
